import type { WarningPattern } from '@/api/warningPatterns/warningPatternQueries';
import { Button } from '@/components/common/Button';
import { DialogWrapper } from '@/components/common/DialogWrapper';
import { Input } from '@/components/common/Input';
import { RadioButton } from '@/components/common/RadioButton';
import { RelationActionButton } from '@/components/common/RelationActionButton';
import type { WarningPatternFormValues } from '@/validation/warningPatternValidator';
import { warningPatternSchema } from '@/validation/warningPatternValidator';
import { useFormik } from 'formik';
import { useEffect } from 'react';
import { toFormikValidate } from 'zod-formik-adapter';

export type { WarningPatternFormValues };

interface WarningPatternDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  pattern?: WarningPattern | null;
  onSubmit: (values: WarningPatternFormValues) => void;
  isPending?: boolean;
}

export function WarningPatternDialog({
  open,
  onOpenChange,
  pattern,
  onSubmit,
  isPending,
}: WarningPatternDialogProps) {
  const isEditing = !!pattern;

  const formik = useFormik<WarningPatternFormValues>({
    initialValues: {
      name: pattern?.name ?? '',
      surname: pattern?.surname ?? '',
      email: pattern?.email ?? '',
      type: pattern?.type ?? true,
    },
    validate: toFormikValidate(warningPatternSchema),
    enableReinitialize: true,
    onSubmit: (values) => {
      onSubmit(values);
    },
  });

  // Reset form when dialog closes
  useEffect(() => {
    if (!open) {
      formik.resetForm();
    }
  }, [open]);

  const hasNameOrSurname = formik.values.name || formik.values.surname;
  const hasEmail = formik.values.email;

  return (
    <DialogWrapper
      open={open}
      onOpenChange={onOpenChange}
      title={isEditing ? 'Edit Warning Pattern' : 'Add New Warning Pattern'}
      maxWidth="4xl"
    >
      <form onSubmit={formik.handleSubmit} className="mt-4 flex flex-col gap-4">
        <div className="text-muted-foreground text-sm">
          Fill in either Name and Surname, or Email (not both).
        </div>

        {/* Name and Surname row */}
        <div className="flex gap-4">
          <div className="flex-1">
            <Input
              id="name"
              name="name"
              label="Name"
              value={formik.values.name}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              error={formik.errors.name}
              touched={formik.touched.name}
              disabled={!!hasEmail}
            />
          </div>
          <div className="flex-1">
            <Input
              id="surname"
              name="surname"
              label="Surname"
              value={formik.values.surname}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              error={formik.errors.surname}
              touched={formik.touched.surname}
              disabled={!!hasEmail}
            />
          </div>
        </div>

        {/* Email row */}
        <Input
          id="email"
          name="email"
          label="Email"
          value={formik.values.email}
          onChange={formik.handleChange}
          onBlur={formik.handleBlur}
          error={formik.errors.email}
          touched={formik.touched.email}
          disabled={!!hasNameOrSurname}
        />

        {/* Match type */}
        <div className="space-y-2">
          <label className="text-table-foreground text-sm font-medium">
            Match Type
          </label>
          <div className="flex gap-6">
            <RadioButton
              id="type-partial"
              name="type"
              label="Part Match"
              checked={!formik.values.type}
              onChange={() => formik.setFieldValue('type', false)}
            />
            <RadioButton
              id="type-exact"
              name="type"
              label="Exact Match"
              checked={formik.values.type}
              onChange={() => formik.setFieldValue('type', true)}
            />
          </div>
        </div>

        <div className="flex items-center justify-between gap-4">
          <Button
            type="submit"
            variant="primary"
            size="md"
            disabled={!formik.isValid || isPending}
          >
            {isPending ? 'Processing...' : isEditing ? 'Save Changes' : 'Add'}
          </Button>
          <RelationActionButton
            type="button"
            onClick={() => onOpenChange(false)}
          >
            Cancel
          </RelationActionButton>
        </div>
      </form>
    </DialogWrapper>
  );
}
